<script>
	// images
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import customerStoriesImg from './images/customer-stories.jpg';
	import selfIndowWindowInstallation from './images/easy-self-indow-window-installation.jpg';
	import mdgBloggraphicInsulatedGlassUnitImg from './images/mdg-bloggraphic-insulatedglassunit.jpeg';
	import solutionLightUvImg from './images/solutions-light-uv.jpg';
</script>

<Section>
	<Image
		src={selfIndowWindowInstallation}
		alt="A close-up image of a person installing window inserts. They are wearing a black dress and have a clear bracelet on their left wrist. "
	/>
	<p>
		Have you noticed a draft coming in from your windows during the colder
		months of the year? If you have old, out-dated windows, they could be
		responsible for a significant amount of heat loss and air leakage in a home.
	</p>
	<p>
		In addition, windows with cracked glass, broken seals, and poor insulation
		cut into the energy efficiency of the home, by overworking the HVAC system
		and driving up energy bills.
	</p>
	<p>
		So, if your home is already having trouble maintaining a comfortable
		temperature for more than a few minutes, and it seems impossible to keep the
		house warm, it may be time to consider new insulation options for the
		windows.
	</p>
</Section>

<Section>
	<h2>6 Ways to Insulate Windows without Replacing Them</h2>
	<h3>1. Install Window Inserts</h3>
	<Image
		src={customerStoriesImg}
		alt="Tall, vertical window framed with old, red brick and white paneling, with a window insert inside the frame."
	/>
	<p>
		Window inserts offer excellent insulation during those frigid winter months
		while being cost-effective compared to full window replacements.
	</p>
	<p>
		Window inserts fit over the existing interior window. Some, <a
			href="/solutions/insulating-drafty-windows">like the ones from Indow</a
		>, are held in place using compression tubing, creating a tight seal around
		the window frame without damaging the window or the frame. Indow inserts are
		made with acrylic, which has much higher insulation properties than glass.
		So, even if installed in front of windows with small cracks or poorly sealed
		windows, the inserts will eliminate drafts and improve the overall energy
		efficiency of the home.
	</p>
	<p>
		The inserts are low-profile and are virtually unnoticeable when installed. A
		great option for historic homes, the inserts are custom measured to fit
		perfectly and won't damage the historic nature of the home, letting you
		preserve their beauty.
	</p>
	<p>
		Inserts not only reduce drafts and help maintain a comfortable temperature
		within the home, but there are also options that can offer significant
		soundproofing capabilities or provide <a href="/products/sleep-panels"
			>light blocking</a
		>
		and <a href="/products/uv-blocking-window-inserts">UV protection</a>. These
		are beneficial to have all year round, as window inserts also provide great
		insulation in the warmer months.
	</p>
	<h3>2. Bubble Wrap</h3>
	<p>
		If the need for a warmer home is urgent, covering the windows with
		<a href="/resources/blog/cold-air-windows">bubble wrap</a> is an option. Bubble
		wrap is not the most aesthetically pleasing or permanent solution, but it is cheap
		and easy to do in a pinch.
	</p>
	<p>
		For the best results, choose a wrap with larger bubbles instead of the
		smaller bubbles, and cut the wrap so it covers the window and overlaps the
		seams. Mist the glass panes with water and apply the bubble side to the damp
		windows. The bubble wrap can be taped into place, but there is a risk that
		the tape will peel the paint off the walls if left for too long. For even
		better insulation, double up the layers of bubble wrap.
	</p>
	<p>
		This will block the window's view, but light will still pass through the
		bubble wrap to brighten your space.
	</p>
	<h3>3. Traditional Caulk</h3>
	<p>
		If you feel a draft coming in through the windows, they may need some
		<a href="/resources/blog/how-to-easily-seal-windows-for-the-winter-months"
			>fresh caulk</a
		>. Caulk is easy to apply and can be used to seal gaps between the window,
		the frame, and the wall.
	</p>
	<p>
		Before applying, it is best to scrape off any old caulk and peeling paint in
		order to achieve a proper seal that will last.
	</p>
	<h3>4. Insulated Glass Units</h3>
	<span>
		<Image
			src={mdgBloggraphicInsulatedGlassUnitImg}
			alt="Two sheets of glass are leaning against a wall, an example of an insulated glass unit."
		/>
		<p>
			<a
				href="https://glassdoctor.com/blog/what-is-an-insulated-glass-unit-or-igu"
				target="_blank"
				rel="noreferrer">source</a
			>
		</p>
	</span>
	<p>
		If your window frame is still in good condition, you could just replace the
		glass with insulated glass units. Insulated glass units are made up of two
		sheets of glass separated by an inert gas. This separation diffuses heat
		transfer in and out of your home, in order to provide more insulation.
	</p>
	<p>
		Sometimes referred to as double-pane glass, insulated glass units are a
		sealed system, so if one of the sheets of glass cracks or breaks, the entire
		unit must be replaced. The unit can be customized depending on how much
		insulation is needed.
	</p>
	<p>
		You can choose thicker or thinner pieces of glass (the thicker the glass,
		the better the insulation), the width of the spacer (the piece that
		separates the glass where they meet at the edge of the window frame), and
		the type of gas that is used between the panes (commonly used gases include
		argon, krypton, or a mixture of the two). Depending on the customization,
		however, these units can get costly fast.
	</p>
	<h3>5. Thermal Curtains</h3>
	<p>
		One of the more design-friendly insulation options is to put up thermal
		curtains. These aesthetically pleasing window coverings have a special
		thermal lining that holds in warm air during the winter and blocks out the
		heat during the summer for year-round use.
	</p>
	<p>
		Thermal curtains work best if they are floor length and wide enough to
		sufficiently cover the window. For further insulation, Velcro tape can be
		added to the curtains and wall for a full seal.
	</p>
	<p>
		These curtains can be used year after year and opened during the warmer
		days. In addition to their insulating capabilities, thermal curtains also
		block out light and act as noise dampeners. However, you'll have to decide
		if you want insulation benefits or sunlight and a view, as you cannot have
		both with these curtains.
	</p>
	<h3>6. Draft Stoppers</h3>
	<p>
		A draft stopper is a snake-like, cloth tube filled with materials like sand,
		beans, rice, or batting. They are typically laid across cracks in doors and
		windows to act as a fabric weatherstripping by preventing cold air from
		leaking in and warm air from escaping out.
	</p>
	<p>
		Draft stoppers can be found at home goods and home improvement stores, but
		it is also an easy and fun DIY project for the crafty crowd. Simply choose
		the preferred fabric, cut and sew it into tubes long enough to cover the
		width of the windowsill and fill it.
	</p>
	<p>
		Draft stoppers do not block the window or prevent it from opening when
		necessary. However, they can only cover the bottom of the window and cannot
		cover the top or the sides. Therefore, they may not be able to provide
		complete insulation if there are cracks throughout the seal.
	</p>
</Section>

<Section>
	<h2>How Indow Can Help</h2>
	<Image
		src={solutionLightUvImg}
		alt="A person is installing a window insert into their window. They are wearing a purple sweater, hoop earrings and brown glasses."
	/>
	<p>
		Indow offers a <a href="/">range of window inserts</a> that fit the needs of every
		home. With our inserts, homeowners don't have to spend replacement window levels
		of money to get effective insulation.
	</p>
	<p>
		Indow window inserts allow you to enjoy the comforts of your home all year
		round. Our easy-to-install solutions not only make your home more efficient
		and safe, but our custom options ensure you get the best fit for your home.
	</p>
	<p>
		Reach out today for a <a href="/how-to/get-indow-inserts">free estimate.</a>
	</p>
</Section>

<Reviews />
